import { useCallback, useRef, useEffect } from 'react'

/**
 * 表格行交互状态接口
 */
interface XCTableRowInteractionState {
  isHovering: boolean
  isEditing: boolean
}

/**
 * 表格行交互检测Hook接口
 */
interface XCTableRowInteractionHook {
  checkInteractionState: () => XCTableRowInteractionState
  waitForInteractionEnd: () => Promise<void>
}

/**
 * 表格行交互检测Hook配置接口
 */
interface XCTableRowInteractionOptions {
  selector?: string
  checkInterval?: number
}

/**
 * 表格行交互检测自定义Hook
 * 用于检测表格行的hover和编辑状态，并提供等待交互结束的功能
 *
 * @param options 配置选项
 * @param options.selector 表格行选择器，默认为第一行
 * @param options.checkInterval 检查间隔时间（毫秒），默认500ms
 * @returns 包含检测函数和等待函数的对象
 */
function useTableRowInteraction(
  options: XCTableRowInteractionOptions = {}
): XCTableRowInteractionHook {
  const {
    selector = '.ant-table-tbody tr:first-child',
    checkInterval = 500
  } = options

  // 使用ref存储当前的定时器ID，避免闭包问题
  const intervalRef = useRef<number | null>(null)

  /**
   * 检测表格行交互状态
   * @returns 包含hover和编辑状态的对象
   */
  const checkInteractionState = useCallback((): XCTableRowInteractionState => {
    const targetRow = document.querySelector(selector)
    if (!targetRow) {
      return { isHovering: false, isEditing: false }
    }

    // 检测hover状态
    const isHovering = targetRow.matches(':hover')

    // 检测编辑状态 - 查找是否有输入框或日期选择器处于激活状态
    const hasActiveInput = targetRow.querySelector(
      'input:focus, textarea:focus, .ant-calendar-picker-open'
    )

    const isEditing = !!hasActiveInput

    return { isHovering, isEditing }
  }, [selector])

  /**
   * 等待表格行交互状态结束
   * @returns Promise，当行不再处于交互状态时resolve
   */
  const waitForInteractionEnd = useCallback(async (): Promise<void> => {
    return new Promise<void>((resolve) => {
      // 清除之前的定时器
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }

      intervalRef.current = window.setInterval(() => {
        const { isHovering, isEditing } = checkInteractionState()

        // 如果既不在hover也不在编辑状态，则结束等待
        if (!isHovering && !isEditing) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          resolve()
        }
      }, checkInterval)
    })
  }, [checkInteractionState, checkInterval])

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    checkInteractionState,
    waitForInteractionEnd
  }
}

export default useTableRowInteraction
export type {
  XCTableRowInteractionState,
  XCTableRowInteractionHook,
  XCTableRowInteractionOptions
}
